import { FormEvent, useId, useState } from 'react';
import { ApiError } from '../../lib/http';
import { StockAlertApi, stockAlertApi } from '../../lib/stockAlertApi';
import './NotifyMe.css';

interface Props {
  readonly itemIds: readonly string[];
  readonly api?: StockAlertApi;
  /** Heading-free intro shown above the field. */
  readonly intro: string;
}

/** A guest leaves an email to hear when sold-out items are back; they confirm by email first. */
export default function NotifyMeForm({ itemIds, api = stockAlertApi, intro }: Props) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState('');
  const hint = useId();
  const errorId = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setState('busy');
    setError('');
    try {
      await api.subscribe(email.trim(), itemIds);
      setState('sent');
    } catch (reason) {
      setState('idle');
      setError(
        reason instanceof ApiError ? reason.message : 'Could not save your request. Please try again.',
      );
    }
  };

  if (state === 'sent') {
    return (
      <p className="notify-me__sent" role="status">
        Check {email.trim()} for a link to confirm. We&apos;ll email you once when{' '}
        {itemIds.length === 1 ? 'it is' : 'they are'} back.
      </p>
    );
  }
  return (
    <form className="notify-me" onSubmit={(event) => void submit(event)}>
      <label className="notify-me__field">
        <span>Email</span>
        <span id={hint} className="notify-me__hint">
          {intro}
        </span>
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-describedby={error ? `${hint} ${errorId}` : hint}
          aria-invalid={error ? true : undefined}
          disabled={state === 'busy'}
        />
      </label>
      {error && (
        <p id={errorId} className="notify-me__error" role="alert">
          {error}
        </p>
      )}
      <button
        className="notify-me__submit"
        type="submit"
        disabled={state === 'busy'}
        aria-busy={state === 'busy'}
      >
        {state === 'busy' ? 'Saving…' : 'Notify me'}
      </button>
    </form>
  );
}
